/**
 * Environment Store
 * 
 * Manages the current environment (Production / Staging) selection
 * Persists to localStorage for user preference
 */

export type Environment = 'production' | 'staging'

// API URLs for each environment
export const API_URLS: Record<Environment, string> = {
  production: 'https://turing.ultronsmart.com',
  staging: 'https://staging-turing.ultronsmart.com'
}

// Environment display info
export const ENVIRONMENT_INFO: Record<Environment, { label: string; color: string; bgColor: string }> = {
  production: {
    label: 'Production',
    color: '#10b981',
    bgColor: 'rgba(16, 185, 129, 0.15)'
  },
  staging: {
    label: 'Staging',
    color: '#f59e0b',
    bgColor: 'rgba(245, 158, 11, 0.15)'
  }
}

const STORAGE_KEY = 'utm_environment'

function resolveDefaultEnvironmentByHostname(): Environment {
  if (typeof window === 'undefined') return 'production'
  const hostname = window.location.hostname.toLowerCase()
  const isPreviewPrHost = hostname.includes('--pr-') && hostname.endsWith('.web.app')
  const isPreviewStagingHost = hostname.includes('--staging-') && hostname.endsWith('.web.app')

  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.includes('staging') ||
    isPreviewStagingHost ||
    isPreviewPrHost
  ) {
    return 'staging'
  }

  return 'production'
}

// Listeners for environment changes
type EnvironmentListener = (env: Environment) => void
const listeners: Set<EnvironmentListener> = new Set()

/**
 * Get current environment from localStorage
 */
export function getCurrentEnvironment(): Environment {
  if (typeof window === 'undefined') return 'production'
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'staging' || stored === 'production') {
    return stored
  }
  
  // No user preference yet: infer from current hostname.
  return resolveDefaultEnvironmentByHostname()
}

/**
 * Set current environment and persist to localStorage
 */
export function setCurrentEnvironment(env: Environment): void {
  if (typeof window === 'undefined') return
  
  localStorage.setItem(STORAGE_KEY, env)
  
  // Notify all listeners
  listeners.forEach(listener => listener(env))
}

/**
 * Get the API URL for the current environment
 */
export function getCurrentApiUrl(): string {
  return API_URLS[getCurrentEnvironment()]
}

/**
 * Subscribe to environment changes
 */
export function subscribeToEnvironment(listener: EnvironmentListener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Check if currently in staging environment
 */
export function isStaging(): boolean {
  return getCurrentEnvironment() === 'staging'
}

/**
 * Toggle between environments
 */
export function toggleEnvironment(): Environment {
  const current = getCurrentEnvironment()
  const next = current === 'production' ? 'staging' : 'production'
  setCurrentEnvironment(next)
  return next
}
